import { useMemo } from "react";
import { useChartAggregate } from "./useChartAggregate";
import type {
  CriteriaFilter,
  DateRangeFilter,
  InsightsAggregateResponse,
  ScoreFilter,
  TagFilter,
} from "../types";
import type { TriageIssue } from "../utils/deriveIssues";
import { bucketRowsToBarPoints } from "../utils/chartData";

/** Smallest score drop worth surfacing as a regression (avoids "-0.00"). */
const REGRESSION_FLOOR = 0.01;

export interface BiggestRegression {
  criteria: string;
  label: string;
  /** prior - current, > 0. */
  drop: number;
  current: number;
  prior: number;
}

export interface InsightsKpis {
  /** Count-weighted mean score across all evaluations in range; null with no data. */
  overallScore: number | null;
  /** Current overall minus the prior period's; null when there's no prior period. */
  overallDelta: number | null;
  /** Total evaluations in the selected range. */
  evaluations: number;
  /** Number of critical (too-often-failing) result groups — matches the rail. */
  needsAttention: number;
  /** The top few critical items, for the "needs attention" tile. */
  attentionItems: TriageIssue[];
  /** The criterion that fell most vs the prior period; null if none declined. */
  biggestRegression: BiggestRegression | null;
}

interface UseInsightsKpisOptions {
  response: InsightsAggregateResponse | null;
  issues: TriageIssue[];
  labels?: Record<string, string>;
  filters: TagFilter[];
  criteriaFilter?: CriteriaFilter;
  dateRangeFilter?: DateRangeFilter;
  scoreFilter?: ScoreFilter;
}

function weightedMean(response: InsightsAggregateResponse | null): number | null {
  let sum = 0;
  let count = 0;
  for (const row of response?.series ?? []) {
    sum += row.avg * row.count;
    count += row.count;
  }
  return count > 0 ? sum / count : null;
}

/**
 * The immediately-preceding window of the same length as [start, end]. Returns
 * null when the range is open-ended (no prior period to compare against).
 */
export function priorPeriod(dr?: DateRangeFilter): DateRangeFilter | null {
  if (!dr?.start_date || !dr?.end_date) return null;
  const start = new Date(dr.start_date + "T00:00:00Z");
  const end = new Date(dr.end_date + "T00:00:00Z");
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const priorEnd = new Date(start);
  priorEnd.setUTCDate(priorEnd.getUTCDate() - 1);
  const priorStart = new Date(start);
  priorStart.setUTCDate(priorStart.getUTCDate() - days);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start_date: iso(priorStart), end_date: iso(priorEnd) };
}

/** The criterion with the largest score drop (>= REGRESSION_FLOOR) vs the prior period. */
export function computeBiggestRegression(
  current: InsightsAggregateResponse | null,
  prior: InsightsAggregateResponse | null,
  labels: Record<string, string> = {},
): BiggestRegression | null {
  if (!current || !prior) return null;
  const priorAvg = new Map(
    bucketRowsToBarPoints(prior.series).map((r) => [r.criteria, r.average]),
  );
  let best: BiggestRegression | null = null;
  for (const cur of bucketRowsToBarPoints(current.series)) {
    const p = priorAvg.get(cur.criteria);
    if (p == null) continue;
    const drop = p - cur.average;
    if (drop >= REGRESSION_FLOOR && (best == null || drop > best.drop)) {
      best = {
        criteria: cur.criteria,
        label: labels[cur.criteria] ?? cur.criteria,
        drop,
        current: cur.average,
        prior: p,
      };
    }
  }
  return best;
}

/** Pure KPI computation over the current + prior responses and the triage issues. */
export function computeKpis(
  current: InsightsAggregateResponse | null,
  prior: InsightsAggregateResponse | null,
  issues: TriageIssue[],
  labels: Record<string, string> = {},
): InsightsKpis {
  const overallScore = weightedMean(current);
  const priorOverall = weightedMean(prior);
  const criticals = issues.filter((i) => i.severity === "critical");
  return {
    overallScore,
    overallDelta:
      overallScore != null && priorOverall != null
        ? overallScore - priorOverall
        : null,
    evaluations: current?.total_evaluations ?? 0,
    needsAttention: criticals.length,
    attentionItems: criticals.slice(0, 2),
    biggestRegression: computeBiggestRegression(current, prior, labels),
  };
}

/**
 * Headline KPIs for the strip at the top of /insights. Overall score and
 * evaluation count are grouping-independent (from the ungrouped response); the
 * needs-attention count reuses the triage issues so the strip and rail agree.
 * A second ungrouped fetch over the prior period powers the week-over-week
 * delta and the biggest-regression tile (skipped for open-ended ranges).
 */
export function useInsightsKpis(opts: UseInsightsKpisOptions): InsightsKpis {
  const {
    response,
    issues,
    labels,
    filters,
    criteriaFilter,
    dateRangeFilter,
    scoreFilter,
  } = opts;

  const prior = useMemo(() => priorPeriod(dateRangeFilter), [dateRangeFilter]);
  const { response: priorResponse } = useChartAggregate({
    filters,
    criteriaFilter,
    dateRangeFilter: prior ?? undefined,
    scoreFilter,
    skip: !prior,
  });

  return useMemo(
    () => computeKpis(response, priorResponse, issues, labels),
    [response, priorResponse, issues, labels],
  );
}
